import type { OnChangeFn } from "@dresscode-frontend/utility-types";
import { isEqual } from "lodash-es";
import type { ComponentType } from "react";
import { useCallback, useMemo } from "react";

export type Filter = Record<string, unknown>;
export type FilterId<TFilter extends Filter> = keyof TFilter & string;

export type FilterProps<TValue = unknown> = {
  value: TValue;
  onChange: OnChangeFn<TValue>;
};

export type FilterDef<
  TFilter extends Filter = Filter,
  TId extends FilterId<TFilter> = FilterId<TFilter>,
  TValue = TFilter[TId],
> = {
  id: TId;
  component: ComponentType<FilterProps<TValue>>;
};

type FilterDefs<TFilter extends Filter> = {
  [K in FilterId<TFilter>]: FilterDef<TFilter, K, TFilter[K]>;
}[FilterId<TFilter>][];

export type ComposableFilter<TFilter extends Filter = Filter> = {
  filterState: TFilter;
  onFilterStateChange: OnChangeFn<TFilter>;
  getFilterDef: <TId extends FilterId<TFilter>>(
    id: TId,
  ) => FilterDef<TFilter, TId, TFilter[TId]>;
  getAllFilterDefs: () => FilterDef<TFilter>[];
  getAppliedFilterCount: () => number;
  getAppliedFilterDefs: () => FilterDef<TFilter>[];
  getOnChangeHandler: <TId extends FilterId<TFilter>>(
    id: TId,
  ) => OnChangeFn<TFilter[TId]>;
  clearAll: () => void;
};

export function useComposableFilter<TFilter extends Filter = Filter>(options: {
  filterDefs: FilterDefs<TFilter>;
  filterState: TFilter;
  onFilterStateChange: OnChangeFn<TFilter>;
}): ComposableFilter<TFilter> {
  const { filterState, onFilterStateChange, filterDefs } = options;

  const filterIdToDef = useMemo(
    () =>
      filterDefs.reduce(
        (acc, def) => {
          acc[def.id] = def;
          return acc;
        },
        {} as Record<FilterId<TFilter>, FilterDef<TFilter>>,
      ),
    [filterDefs],
  );

  const getFilterDef = useCallback(
    <TId extends FilterId<TFilter>>(
      id: TId,
    ): FilterDef<TFilter, TId, TFilter[TId]> =>
      filterIdToDef[id] as unknown as FilterDef<TFilter, TId, TFilter[TId]>,
    [filterIdToDef],
  );

  const getAllFilterDefs = useCallback(() => filterDefs, [filterDefs]);

  const getAppliedFilterCount = useCallback(
    () => filterDefs.filter(({ id }) => filterState[id] !== undefined).length,
    [filterDefs, filterState],
  );

  const getAppliedFilterDefs = useCallback(
    () => filterDefs.filter(({ id }) => filterState[id] !== undefined),
    [filterDefs, filterState],
  );

  const getOnChangeHandler = useMemo(() => {
    const memo = new Map<FilterId<TFilter>, OnChangeFn>();
    return <TId extends FilterId<TFilter>>(id: TId) => {
      if (!memo.has(id)) {
        memo.set(id, (value: unknown) => {
          if (!isEqual(filterState[id], value)) {
            onFilterStateChange((prev) => ({ ...prev, [id]: value }));
          }
        });
      }
      return memo.get(id) as OnChangeFn<TFilter[TId]>;
    };
  }, [onFilterStateChange, filterState]);

  const clearAll = useCallback(() => {
    const newState = filterDefs.reduce(
      (acc, def) => {
        acc[def.id] = undefined;
        return acc;
      },
      {} as Record<FilterId<TFilter>, undefined>,
    );
    onFilterStateChange(newState as TFilter);
  }, [onFilterStateChange, filterDefs]);

  return {
    filterState,
    onFilterStateChange,
    getFilterDef,
    getAllFilterDefs,
    getAppliedFilterCount,
    getAppliedFilterDefs,
    getOnChangeHandler,
    clearAll,
  };
}

export function createFilterDefHelper<TFilter extends Filter>() {
  return function filterDefHelper<TId extends FilterId<TFilter>>(
    id: TId,
    options: { component: ComponentType<FilterProps<TFilter[TId]>> },
  ): FilterDef<TFilter, TId, TFilter[TId]> {
    return {
      id,
      component: options.component,
    };
  };
}
